import { Box, Button, Flex, Image, Stack, Text, ThemeIcon } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconPhoto, IconUpload, IconX } from '@tabler/icons-react';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { BrandAssetBuilderData } from 'src/secret-menu/brand-asset-builder/types';

const MAX_LOGO_SIZE = 5 * 1024 * 1024;

const ACCEPTED_LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export default function UploadLogoStep(props: StepProps<BrandAssetBuilderData>) {
  const { data, setData } = props;

  const handleDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (data.logoUrl) {
      URL.revokeObjectURL(data.logoUrl);
    }
    setData({ ...data, logoFile: file, logoUrl: URL.createObjectURL(file) });
  };

  const handleRemove = () => {
    if (data.logoUrl) {
      URL.revokeObjectURL(data.logoUrl);
    }
    setData({ ...data, logoFile: null, logoUrl: null });
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Upload logo"
      description="Upload the tenant's logo. We'll use it to extract a colour palette and place it on the title page."
      canGoForward={data.logoFile !== null}
      showClientName={false}
      hideProgress
      allowVerticalOverflow
    >
      <Stack gap="lg">
        {data.logoFile && data.logoUrl ? (
          <Flex align="center" gap="md" p="sm" bg="gray.0" style={{ borderRadius: 8 }}>
            <Box
              w={96}
              h={96}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Image src={data.logoUrl} alt="Tenant logo" fit="contain" h={96} />
            </Box>
            <Stack gap={2} flex={1}>
              <Text size="textSm" fw={FONT_WEIGHTS.medium}>
                {data.logoFile.name}
              </Text>
              <Text size="textSm" c="gray.6">
                {(data.logoFile.size / 1024).toFixed(0)} KB
              </Text>
            </Stack>
            <Button variant="subtle" color="red" size="xs" onClick={handleRemove}>
              Remove
            </Button>
          </Flex>
        ) : (
          <Dropzone
            onDrop={handleDrop}
            onReject={() =>
              notifications.show({
                title: 'Rejected',
                message: 'Logo must be an image under 5MB.',
                color: 'red',
              })
            }
            maxSize={MAX_LOGO_SIZE}
            maxFiles={1}
            accept={ACCEPTED_LOGO_MIME_TYPES}
            style={{
              border: '1px dashed var(--mantine-color-gray-4)',
              borderRadius: 12,
              padding: 40,
            }}
          >
            <Flex direction="column" align="center" gap="md">
              <Dropzone.Accept>
                <ThemeIcon variant="light" color="blue" size={56} radius="xl">
                  <IconUpload size={28} />
                </ThemeIcon>
              </Dropzone.Accept>
              <Dropzone.Reject>
                <ThemeIcon variant="light" color="red" size={56} radius="xl">
                  <IconX size={28} />
                </ThemeIcon>
              </Dropzone.Reject>
              <Dropzone.Idle>
                <ThemeIcon variant="light" color="gray" size={56} radius="xl">
                  <IconPhoto size={28} />
                </ThemeIcon>
              </Dropzone.Idle>
              <Text fw={FONT_WEIGHTS.semibold} size="textLg">
                Drop the tenant&apos;s logo here
              </Text>
              <Text c="gray.6" size="textSm">
                or click to browse — PNG, JPG, GIF or WebP, max 5MB
              </Text>
            </Flex>
          </Dropzone>
        )}
      </Stack>
    </DrawerFlowScreenLayout>
  );
}
